import { CopyOnClick } from '@/components/planilha/CopyOnClick';
import { formatarDocumento } from '@/components/kanban/triagem/segmentoTriagem';
import { Badge } from '@/components/ui/badge';
import { usePainelPedido } from '@/contexts/PainelPedidoContext';
import { toBrasilia } from '@/lib/dateUtils';
import { TempoTriagemBadge } from '@/components/kanban/TempoTriagemBadge';
import { PrazoSlaBadge } from '@/components/kanban/PrazoSlaBadge';
import { TRIAGEM_SELO_BASE, TRIAGEM_SELO_PENDENTE, TRIAGEM_TD, TRIAGEM_TH } from '@/components/kanban/triagem/triagemTabela';

// ─── Shared interface ───────────────────────────────────────────────
export interface TriagemStandardItem {
  item_id: string;
  pedido_id: string;
  numero_pedido: string;
  item_seq: number | null;
  cnpj: string;
  /** CNPJ ou CPF — a fila mostra um segmento por vez (`segmentoTriagem.ts`). */
  tipo_pessoa: string | null;
  /** CNPJ ou CPF, como está no banco. Máscara em `formatarDocumento`. */
  documento: string;
  razao_social: string;
  numero_cotacao_operadora: string;
  linhas_formatadas: string[];
  operadora_doadora: string;
  plano_nome: string;
  tipo_chip: string;
  faturamento_tipo: string;
  entrada_coluna_em: string;
  agenda_tipo: string;
  data_agendamento: string;
  cessionario_nome: string;
  cessionario_cpf: string;
  is_first_of_cnpj: boolean;
  cliente_id: string;
  /** A coluna do item: a coluna Tempo lê o prazo da fila (`sla_filas`). */
  coluna_atual?: string | null;
}

// ─── Standard select fields to add to v_planilha_mae queries ────────
export const STANDARD_SELECT_FIELDS = 'item_id, pedido_id, numero_pedido, item_seq, cnpj, razao_social, tipo_chip, telefone_principal, contato_real_email, cliente_id, entrada_coluna_em, status_id, coluna_atual, quadro_atual, manter_em, numero_cotacao_operadora, linhas_telefone, plano_nome, faturamento_tipo, agenda_tipo, agenda_data, data_agendamento, contato_real_telefone, cessionario_nome, cessionario_cpf, ddd, linha, operadora_doadora, bko_portabilidade_id, owner_qualidade_id, tipo_pessoa, documento';

// ─── Utility functions ──────────────────────────────────────────────
export function formatLinhasTelefone(raw: any): string[] {
  if (!raw) return [];
  let parsed = raw;
  if (typeof raw === 'string') {
    try { parsed = JSON.parse(raw); } catch { return [raw]; }
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.map((l: any) => {
    if (typeof l === 'string') return l;
    if (l && typeof l === 'object') {
      const ddd = l.ddd || l.DDD || '';
      const linha = l.linha || l.numero || l.LINHA || '';
      if (ddd && linha) return `(${ddd}) ${linha}`;
      if (linha) return linha;
      if (ddd) return `(${ddd})`;
      return JSON.stringify(l);
    }
    return String(l);
  });
}

export function buildLinhasFormatadas(item: any): string[] {
  const fromJson = formatLinhasTelefone(item.linhas_telefone);
  if (fromJson.length > 0) return fromJson;
  if (item.ddd && item.linha) {
    return [`(${item.ddd}) ${item.linha}`];
  }
  return [];
}

export function getOperadoraFromLinhas(raw: any): string {
  let parsed = raw;
  if (typeof raw === 'string') {
    try { parsed = JSON.parse(raw); } catch { return '-'; }
  }
  if (!parsed || !Array.isArray(parsed)) return '-';
  const ops = new Set<string>();
  parsed.forEach((l: any) => {
    if (l && typeof l === 'object') {
      const op = l.operadora_doadora || l.operadora || l.OPERADORA || '';
      if (op) ops.add(String(op).toUpperCase());
    }
  });
  if (ops.size === 0) return '-';
  if (ops.size === 1) return [...ops][0];
  return 'Várias';
}

export function getOperadoraWithFallback(item: any): string {
  if (item.operadora_doadora) return String(item.operadora_doadora).toUpperCase();
  return getOperadoraFromLinhas(item.linhas_telefone);
}

// formatTempoNaFila removida — use TempoTriagemBadge
/** ⚠️ Mascara CNPJ **e CPF** — régua única em `segmentoTriagem`. */
export function formatCnpj(cnpj: string): string {
  return formatarDocumento(cnpj);
}

export function mapToStandardItem(item: any): Omit<TriagemStandardItem, 'is_first_of_cnpj'> {
  return {
    item_id: item.item_id,
    pedido_id: item.pedido_id,
    numero_pedido: String(item.numero_pedido || ''),
    item_seq: item.item_seq ?? null,
    cnpj: item.cnpj || '',
    tipo_pessoa: item.tipo_pessoa ?? null,
    documento: item.documento || item.cnpj || '',
    razao_social: item.razao_social || '',
    numero_cotacao_operadora: item.numero_cotacao_operadora || '',
    linhas_formatadas: buildLinhasFormatadas(item),
    operadora_doadora: getOperadoraWithFallback(item),
    plano_nome: item.plano_nome || '',
    tipo_chip: item.tipo_chip || '',
    faturamento_tipo: item.faturamento_tipo || '',
    entrada_coluna_em: item.entrada_coluna_em || '',
    agenda_tipo: item.agenda_tipo || '',
    data_agendamento: item.data_agendamento || '',
    cessionario_nome: item.cessionario_nome || '',
    cessionario_cpf: item.cessionario_cpf || '',
    cliente_id: item.cliente_id || '',
  };
}

// ─── Standard ColGroup ──────────────────────────────────────────────
export function TriagemStandardColGroup({ extraCols }: { extraCols?: React.ReactNode }) {
  return (
    <colgroup>
      <col style={{ width: '9%' }} />   {/* CNPJ */}
      <col style={{ width: '15%' }} />  {/* Razão Social */}
      <col style={{ width: '7%' }} />   {/* Pedido */}
      <col style={{ width: '5%' }} />   {/* Cotação */}
      <col style={{ width: '8%' }} />   {/* Linhas */}
      <col style={{ width: '4%' }} />   {/* Oper. */}
      <col style={{ width: '11%' }} />  {/* Plano */}
      <col style={{ width: '4%' }} />   {/* Chip */}
      <col style={{ width: '3%' }} />   {/* Tipo */}
      <col style={{ width: '3%' }} />   {/* PN */}
      <col style={{ width: '4%' }} />   {/* Tempo */}
      <col style={{ width: '4%' }} />   {/* Agenda */}
      <col style={{ width: '10%' }} />  {/* Cession. */}
      {extraCols}
    </colgroup>
  );
}

// ─── Standard Headers ───────────────────────────────────────────────
/* Anatomia unica das 23 triagens — antes era `py-1.5 px-1 font-medium`, que
   herdava o 12px/uppercase do <tr> e nao batia com a Planilha. */
const TH = TRIAGEM_TH;

export function TriagemStandardHeaders({ rotuloDocumento = 'CNPJ' }: { rotuloDocumento?: string } = {}) {
  return (
    <>
      {/* O rótulo segue o segmento da fila: em CPF a coluna se chama CPF, e o
          documento aparece mascarado como CPF (`formatarDocumento`). */}
      <th className={TH}>{rotuloDocumento}</th>
      <th className={TH}>Razão Social</th>
      <th className={TH}>Pedido</th>
      <th className={TH}>Cotação</th>
      <th className={TH}>Linhas</th>
      <th className={TH}>Oper.</th>
      <th className={TH}>Plano</th>
      <th className={TH}>Chip</th>
      <th className={TH}>Tipo</th>
      <th className={TH}>PN</th>
      <th className={TH}>Tempo</th>
      <th className={TH}>Agenda</th>
      <th className={TH}>Cession.</th>
    </>
  );
}

// ─── Standard Cells ─────────────────────────────────────────────────
export function TriagemStandardCells({ item }: { item: TriagemStandardItem }) {
  const { open: openPedido } = usePainelPedido();

  const tipoChipLabel = item.tipo_chip?.toLowerCase().includes('esim') ? 'eSIM' :
    item.tipo_chip && item.tipo_chip !== '-' && item.tipo_chip !== '' ? 'Físico' : '-';

  const tipoBadge = (() => {
    const t = (item.faturamento_tipo || '').toUpperCase();
    if (t.includes('NOVA') || t.includes('VENDA')) return { label: 'VN', cls: 'bg-muted text-muted-foreground' };
    if (t.includes('MIGRA')) return { label: 'MIG', cls: 'bg-muted text-muted-foreground' };
    if (t.includes('PORT')) return { label: 'PORT', cls: 'bg-muted text-primary dark:bg-muted/30 dark:text-primary' };
    if (t) return { label: t, cls: 'bg-muted text-muted-foreground' };
    return null;
  })();

  const agendaText = (() => {
    const raw = item.data_agendamento;
    if (!raw) return '—';
    const d = new Date(raw.includes('T') ? raw : raw + 'T00:00:00');
    const br = toBrasilia(d);
    const date = br.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    return item.agenda_tipo ? `${date} ${item.agenda_tipo}` : date;
  })();

  return (
    <>
      {/* CNPJ */}
      <td className={TRIAGEM_TD}>
        {item.is_first_of_cnpj ? (
          <CopyOnClick value={item.documento || item.cnpj} className="tabular-nums text-[10px] truncate block">
            {formatCnpj(item.documento || item.cnpj)}
          </CopyOnClick>
        ) : null}
      </td>
      {/* Razão Social */}
      <td className={TRIAGEM_TD}>
        <span className="block truncate text-[12px] font-semibold text-foreground" title={item.razao_social}>
          {item.razao_social || '-'}
        </span>
      </td>
      {/* Pedido Item */}
      <td className={TRIAGEM_TD}>
        <button
          onClick={(e) => { e.stopPropagation(); openPedido(item.pedido_id, 'visao360'); }}
          className="block truncate text-[11px] font-bold tabular-nums text-primary hover:underline cursor-pointer"
        >
          {item.numero_pedido}{item.item_seq ? `-${item.item_seq}` : ''}
        </button>
      </td>
      {/* Cotação */}
      <td className={TRIAGEM_TD}>
        {item.numero_cotacao_operadora ? (
          <CopyOnClick value={item.numero_cotacao_operadora} className="tabular-nums text-[10px] truncate block">
            {item.numero_cotacao_operadora}
          </CopyOnClick>
        ) : (
          <span className="text-[11px] text-muted-foreground/40">—</span>
        )}
      </td>
      {/* Linhas */}
      <td className={TRIAGEM_TD}>
        {/* Antes empilhava um <span> por telefone e ESTICAVA a linha (item com 4
            linhas virava 4x a altura e desalinhava a tabela inteira). Agora e' uma
            linha so'; o title entrega todos. */}
        {/* ⛔ HA não finaliza sem o número (Douglas 30/09/2026): "Número Novo" no
            Pré-Ativo é dívida da BKO, não informação — vira selo bordô. */}
        {item.linhas_formatadas.some((l) => /n[úu]mero novo/i.test(l)) ? (
          <span
            className={`${TRIAGEM_SELO_BASE} ${TRIAGEM_SELO_PENDENTE} max-w-full truncate`}
            title="Linha HA sem o número. Ao finalizar, a BKO coloca o número que está no SOLAR."
          >
            {item.linhas_formatadas.map((l) => l.replace(/n[úu]mero novo/i, 'falta o número')).join(' · ')}
          </span>
        ) : item.linhas_formatadas.length > 0 ? (
          <span
            className="block truncate text-[10px] tabular-nums text-muted-foreground"
            title={item.linhas_formatadas.join(' · ')}
          >
            {item.linhas_formatadas.join(' · ')}
          </span>
        ) : (
          <span className="text-[11px] text-muted-foreground/40">—</span>
        )}
      </td>
      {/* Operadora */}
      <td className={TRIAGEM_TD}>
        <span className="text-[10px] text-muted-foreground truncate block">{item.operadora_doadora === '-' ? '—' : item.operadora_doadora}</span>
      </td>
      {/* Plano */}
      <td className={TRIAGEM_TD}>
        <span className="text-xs text-muted-foreground truncate block" title={item.plano_nome}>
          {item.plano_nome || '-'}
        </span>
      </td>
      {/* Chip */}
      <td className={TRIAGEM_TD}>
        <span className={`text-[10px] font-medium text-muted-foreground`}>
          {tipoChipLabel}
        </span>
      </td>
      {/* Tipo */}
      <td className={TRIAGEM_TD}>
        {tipoBadge ? (
          <Badge variant="outline" className={`text-[10px] px-1 py-0 border-0 ${tipoBadge.cls}`}>
            {tipoBadge.label}
          </Badge>
        ) : (
          <span className="text-[11px] text-muted-foreground/40">—</span>
        )}
      </td>
      {/* PN/PJ */}
      <td className={TRIAGEM_TD}>
        {(() => {
          const digits = (item.cnpj || '').replace(/\D/g, '');
          const isPn = digits.length === 11;
          const isPj = digits.length === 14;
          if (isPn) return <Badge variant="outline" className="text-[10px] px-1 py-0 border-0 bg-accent font-bold text-primary">PN</Badge>;
          if (isPj) return <Badge variant="outline" className="text-[10px] px-1 py-0 border-0 bg-muted text-muted-foreground">PJ</Badge>;
          return <span className="text-[11px] text-muted-foreground/40">—</span>;
        })()}
      </td>
      {/* Tempo */}
      <td className={TRIAGEM_TD}>
        <PrazoSlaBadge
          pedidoId={item.pedido_id}
          coluna={item.coluna_atual}
          entradaColunaEm={item.entrada_coluna_em}
          prazoHoras={5}
        />
      </td>
      {/* Agenda */}
      <td className={TRIAGEM_TD}>
        <span className="text-[10px] text-muted-foreground truncate block">{agendaText}</span>
      </td>
      {/* Cessionário */}
      <td className={TRIAGEM_TD}>
        {item.cessionario_nome ? (
          <span
            className="block truncate text-[10px] text-muted-foreground"
            title={`${item.cessionario_nome}${item.cessionario_cpf ? ` · ${item.cessionario_cpf}` : ''}`}
          >
            {item.cessionario_nome}
            {item.cessionario_cpf ? ` · ${item.cessionario_cpf}` : ''}
          </span>
        ) : (
          <span className="text-[11px] text-muted-foreground/40">—</span>
        )}
      </td>
    </>
  );
}
